import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput } from '@/components/ui/Input';
import { LockIcon, ShieldCheckIcon } from '@/components/ui/Icons';

/**
 * The auth form kit. These names are unchanged from the original file so every
 * auth page keeps working — only the visuals moved onto the design system.
 */

export function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    // The auth pages render straight into <main>, so the page framing lives here.
    <div className="mx-auto w-full max-w-md px-5 py-12 sm:py-16">
      <div className="rounded-2xl border border-line bg-white p-7 shadow-lift sm:p-8">
        <h1 className="text-2xl font-extrabold text-ink">{title}</h1>
        {subtitle && <p className="mt-2 text-sm leading-relaxed text-slate-500">{subtitle}</p>}
        <div className="mt-7 space-y-5">{children}</div>
      </div>

      <p className="mt-5 flex items-center justify-center gap-2 text-xs font-medium text-slate-500">
        <LockIcon className="size-4 text-safe-600" />
        Secure, verified and parent-approved by design
      </p>
    </div>
  );
}

export function Field({
  label,
  hint,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return <Input label={label} hint={hint} {...props} />;
}

export function Select({
  label,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <SelectInput label={label} {...props}>
      {children}
    </SelectInput>
  );
}

export function SubmitButton({ children, busy }: { children: ReactNode; busy?: boolean }) {
  return (
    <Button type="submit" size="lg" block disabled={busy} aria-busy={busy || undefined}>
      {busy ? 'Working…' : children}
    </Button>
  );
}

export function ErrorNote({
  message,
  action,
}: {
  message: string | null;
  /** Optional follow-up, e.g. a link to sign in when the account exists. */
  action?: ReactNode;
}) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm font-medium text-red-700"
    >
      {message}
      {action ? <> {action}</> : null}
    </p>
  );
}

export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-xl border border-brand-100 bg-brand-50 px-3.5 py-2.5 text-sm text-brand-800">
      <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
